import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  Card,
  Col,
  Empty,
  Form,
  Input,
  List,
  message,
  Popconfirm,
  Row,
  Select,
  Space,
  Spin,
  Tag,
  Typography
} from 'antd';
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  FormOutlined,
  PauseCircleOutlined
} from '@ant-design/icons';
import { classAPI, classTodoAPI } from '../../services/api';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

const PRIORITY_OPTIONS = [
  { label: '高优先级', value: 1, color: 'red' },
  { label: '中优先级', value: 2, color: 'gold' },
  { label: '低优先级', value: 3, color: 'blue' }
];

const STATUS_OPTIONS = [
  { label: '立即显示', value: 'active' },
  { label: '先保存为停用', value: 'inactive' }
];

const ClassTodoManagePage = () => {
  const { classId } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();

  const [classInfo, setClassInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [listLoading, setListLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [items, setItems] = useState([]);
  const [editingItem, setEditingItem] = useState(null);

  const titleText = useMemo(
    () => (editingItem ? '编辑班级待办事项' : '发送班级待办事项'),
    [editingItem]
  );

  useEffect(() => {
    if (classId) {
      initPage();
    }
  }, [classId]);

  const initPage = async () => {
    try {
      setLoading(true);
      const [classRes, todoRes] = await Promise.all([
        classAPI.getById(classId),
        classTodoAPI.getManageList(classId)
      ]);

      if (classRes.data?.success) {
        setClassInfo(classRes.data.data);
      } else {
        message.error('获取班级信息失败');
        navigate('/classes/todos');
        return;
      }

      if (todoRes.data?.success) {
        setItems(todoRes.data.data?.items || []);
      }
    } catch (error) {
      console.error('初始化班级待办事项页面失败:', error);
      message.error('加载班级待办事项失败');
      navigate('/classes/todos');
    } finally {
      setLoading(false);
    }
  };

  const loadTodos = async () => {
    try {
      setListLoading(true);
      const response = await classTodoAPI.getManageList(classId);
      if (response.data?.success) {
        setItems(response.data.data?.items || []);
      } else {
        message.error(response.data?.message || '获取待办事项失败');
      }
    } catch (error) {
      console.error('获取班级待办事项失败:', error);
      message.error(error.response?.data?.message || '获取待办事项失败');
    } finally {
      setListLoading(false);
    }
  };

  const resetForm = () => {
    setEditingItem(null);
    form.setFieldsValue({
      title: '',
      content: '',
      priority: 2,
      status: 'active'
    });
  };

  const handleSubmit = async (values) => {
    try {
      setSaving(true);
      const payload = {
        title: values.title,
        content: values.content,
        priority: values.priority,
        status: values.status
      };

      const response = editingItem
        ? await classTodoAPI.update(editingItem.id, payload)
        : await classTodoAPI.create(classId, payload);

      if (response.data?.success) {
        message.success(editingItem ? '班级待办事项已更新' : '班级待办事项已发送');
        resetForm();
        await loadTodos();
        return;
      }

      message.error(response.data?.message || '保存失败');
    } catch (error) {
      console.error('保存班级待办事项失败:', error);
      message.error(error.response?.data?.message || '保存失败');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    form.setFieldsValue({
      title: item.title || '',
      content: item.content || '',
      priority: item.priority || 2,
      status: item.status || 'active'
    });
  };

  const handleToggleStatus = async (item) => {
    try {
      const nextStatus = item.status === 'active' ? 'inactive' : 'active';
      const response = await classTodoAPI.updateStatus(item.id, nextStatus);
      if (response.data?.success) {
        message.success(nextStatus === 'active' ? '已启用显示' : '已停用显示');
        await loadTodos();
      } else {
        message.error(response.data?.message || '更新状态失败');
      }
    } catch (error) {
      console.error('切换班级待办事项状态失败:', error);
      message.error(error.response?.data?.message || '更新状态失败');
    }
  };

  const handleDelete = async (item) => {
    try {
      const response = await classTodoAPI.delete(item.id);
      if (response.data?.success) {
        message.success('班级待办事项已删除');
        if (editingItem?.id === item.id) {
          resetForm();
        }
        await loadTodos();
      } else {
        message.error(response.data?.message || '删除失败');
      }
    } catch (error) {
      console.error('删除班级待办事项失败:', error);
      message.error(error.response?.data?.message || '删除失败');
    }
  };

  const priorityMeta = (priority) =>
    PRIORITY_OPTIONS.find((item) => item.value === Number(priority)) || PRIORITY_OPTIONS[1];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 0' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'center' }}>
          <Space align="start">
            <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate('/classes/todos')}>
              返回
            </Button>
            <FormOutlined style={{ fontSize: 24, color: '#1890ff', marginTop: 4 }} />
            <div>
              <Title level={4} style={{ margin: 0 }}>
                {classInfo?.name || '班级'} - 班级待办事项
              </Title>
              <Text type="secondary">
                {classInfo?.school_name || '未同步学校'}{classInfo?.grade ? ` · ${classInfo.grade}` : ''}
              </Text>
            </div>
          </Space>
          <Button onClick={loadTodos} loading={listLoading}>
            刷新列表
          </Button>
        </div>

        <Row gutter={24}>
          <Col xs={24} lg={10}>
            <Card size="small" title={titleText} style={{ marginBottom: 16 }}>
              <Form
                form={form}
                layout="vertical"
                initialValues={{ title: '', content: '', priority: 2, status: 'active' }}
                onFinish={handleSubmit}
              >
                <Form.Item
                  label="待办标题"
                  name="title"
                  rules={[{ required: true, message: '请输入待办标题' }]}
                >
                  <Input maxLength={120} placeholder="例如：第一节课前完成课桌整理" />
                </Form.Item>

                <Form.Item label="待办内容" name="content">
                  <TextArea rows={5} maxLength={500} placeholder="例如：请同学们课前检查课本、练习册与文具，安静等待上课。" />
                </Form.Item>

                <Form.Item label="优先级" name="priority">
                  <Select options={PRIORITY_OPTIONS} />
                </Form.Item>

                <Form.Item label="显示状态" name="status">
                  <Select options={STATUS_OPTIONS} />
                </Form.Item>

                <Space>
                  <Button type="primary" htmlType="submit" loading={saving}>
                    {editingItem ? '保存修改' : '发送到班级'}
                  </Button>
                  <Button onClick={resetForm}>
                    {editingItem ? '取消编辑' : '清空内容'}
                  </Button>
                </Space>
              </Form>
            </Card>

            <Card size="small" title="说明">
              <Paragraph style={{ marginBottom: 8 }}>
                班级待办事项会显示在桌面客户端欢迎页的“班级待办事项”卡片中。
              </Paragraph>
              <Paragraph style={{ marginBottom: 0 }}>
                状态为“立即显示”的内容会立刻被教室端读取；停用后将不再显示。
              </Paragraph>
            </Card>
          </Col>

          <Col xs={24} lg={14}>
            <Card size="small" title={`已发送待办事项 (${items.length})`}>
              {items.length === 0 ? (
                <Empty description="当前班级还没有待办事项" />
              ) : (
                <List
                  loading={listLoading}
                  itemLayout="vertical"
                  dataSource={items}
                  renderItem={(item) => {
                    const priority = priorityMeta(item.priority);
                    return (
                      <List.Item
                        actions={[
                          <Button key="edit" type="link" onClick={() => handleEdit(item)}>
                            编辑
                          </Button>,
                          <Button
                            key="toggle"
                            type="link"
                            icon={item.status === 'active' ? <PauseCircleOutlined /> : <CheckCircleOutlined />}
                            onClick={() => handleToggleStatus(item)}
                          >
                            {item.status === 'active' ? '停用' : '启用'}
                          </Button>,
                          <Popconfirm
                            key="delete"
                            title="确定删除这条班级待办事项吗？"
                            onConfirm={() => handleDelete(item)}
                            okText="删除"
                            cancelText="取消"
                          >
                            <Button type="link" danger>
                              删除
                            </Button>
                          </Popconfirm>
                        ]}
                      >
                        <Space wrap style={{ marginBottom: 8 }}>
                          <Tag color={priority.color}>{priority.label}</Tag>
                          <Tag color={item.status === 'active' ? 'green' : 'default'}>
                            {item.status === 'active' ? '显示中' : '已停用'}
                          </Tag>
                        </Space>
                        <Title level={5} style={{ marginTop: 0, marginBottom: 8 }}>
                          {item.title}
                        </Title>
                        <Paragraph style={{ marginBottom: 8 }}>
                          {item.content || '暂无详细说明'}
                        </Paragraph>
                        <Text type="secondary">
                          更新时间：{item.updated_at ? String(item.updated_at).replace('T', ' ').slice(0, 19) : '刚刚'}
                        </Text>
                      </List.Item>
                    );
                  }}
                />
              )}
            </Card>
          </Col>
        </Row>
      </Card>
    </div>
  );
};

export default ClassTodoManagePage;
